import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import Iconify from "../../components/iconify/Iconify";
import EmptyVariantState from "./EmptyVariantState";
import ItemVariantImageUploadDialog from "./ItemVariantImageUploadDialog";

const ItemVariant = ({
  variants,
  setVariants,
  deletedVariants,
  setDeletedVariants,
  attributesKeyValue,
  selectedAttributes,
  setSelectedAttributes,
  isItemWithVariant,
}) => {
  const { units } = useSelector((state) => state.item);
  const [selectImageDialogOpen, setSelectImageDialogOpen] = useState(false);

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  const handleUploadClick = () => {
    setSelectImageDialogOpen(true);
  };

  // Add selected attribute as a new variant row
  const handleAddVariantRow = () => {
    if (selectedAttributes.length === 0) return;

    // Build attributes for this new variant
    const initializedAttributes = selectedAttributes.map((attr) => ({
      attribute_uid: attr.uid,
      attribute_name: attr.name,
      attribute_value_uid: null,
      attribute_value: null,
      is_new_record: 1,
      record_status: "A",
    }));

    const newVariant = {
      attributes: initializedAttributes,
      original_price: "",
      discounted_price: "",
      sku_code: "",
      combo_quantity: null,
      unit_value: 1,
      selected_unit: null,
      image: "",
      is_new_record: 1,
      record_status: "A",
      deleted_attributes: [], // important for update mode
    };

    setVariants((prev) => [newVariant, ...prev]);
  };

  const handleSelectAttributesChange = (event, newValue) => {
    setSelectedAttributes(newValue);

    console.log("xxxxx", variants);

    if (!variants.length) return;
    const updatedVariants = variants.map((variant) => {
      const updatedAttributes = [];
      const deletedAttributes = [...(variant.deleted_attributes || [])];

      // --- 1. Handle removed attributes ---
      variant.attributes.forEach((attr) => {
        const stillSelected = newValue.find(
          (a) => a.uid === attr.attribute_uid
        );

        if (!stillSelected) {
          // This attribute is removed from the UI selection

          if (attr.variant_attribute_uid) {
            // Attribute came from DB → must be soft-deleted
            deletedAttributes.push({
              ...attr,
              record_status: "R",
            });
          }

          // If attribute is new (no UID), we simply don't keep it
        } else {
          // Keep attributes still selected
          updatedAttributes.push(attr);
        }
      });

      // --- 2. Add new attributes (just selected) ---
      newValue.forEach((attr) => {
        const exists = updatedAttributes.find(
          (a) => a.attribute_uid === attr.uid
        );

        if (!exists) {
          updatedAttributes.push({
            attribute_uid: attr.uid,
            attribute_name: attr.name,
            attribute_value_uid: null,
            attribute_value: null,
            is_new_record: 1,
          });
        }
      });

      return {
        ...variant,
        attributes: updatedAttributes,
        deleted_attributes: deletedAttributes,
      };
    });

    setVariants(updatedVariants);
  };

  const handleRemoveVariant = (indexToDelete) => {
    const variantToDelete = variants[indexToDelete];
    setVariants((prev) => prev.filter((_, i) => i !== indexToDelete));
    setDeletedVariants((prev) => [
      ...prev,
      { ...variantToDelete, record_status: "R" },
    ]);
  };

  const handleFormValueChange = (event, index, newValue) => {
    const { name, value } = event.target || {};

    const numericFields = [
      "original_price",
      "discounted_price",
      "combo_quantity",
      "unit_value",
    ];

    setVariants((prev) =>
      prev.map((v, i) => {
        if (i !== index) return v;

        let finalValue = value !== undefined ? value : newValue;

        // Convert to number if it's a numeric field
        if (numericFields.includes(name)) {
          finalValue = finalValue === "" ? "" : Number(finalValue);
        }
        return {
          ...v,
          [name]: finalValue,
        };
      })
    );
  };

  const getSelectedAttrValue = (variant, attribute) => {
    const found = variant.attributes.find(
      (a) => a.attribute_uid === attribute.uid
    );

    if (!found) return null;

    // Find the attribute value by its correct UID
    return (
      attribute.attributeValues?.find(
        (v) => v.attribute_uid === found.attribute_value_uid
      ) || null
    );
  };

  const handleVariantAttributeValueChange = (
    variantIndex,
    attribute, // pass full attribute object
    selectedValue // selected attribute value object
  ) => {
    console.log("variantsyyyyyyyyy", variants);
    const updatedVariants = [...variants];
    const variant = { ...updatedVariants[variantIndex] };
    const updatedAttributes = variant.attributes.map((attr) => {
      if (attr.attribute_uid !== attribute.uid) return attr;

      return {
        ...attr,
        attribute_value_uid: selectedValue?.attribute_uid || null,
        attribute_value: selectedValue?.attribute_value || "",
        is_new_record: attr.variant_attribute_uid ? 0 : 1,
        record_status: "A",
      };
    });

    variant.attributes = updatedAttributes;
    updatedVariants[variantIndex] = variant;
    console.log("updatedVariants", updatedVariants);
    setVariants(updatedVariants);
  };

  return (
    <Box>
      {/* Attribute selection */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ width: "40%", minWidth: 300 }}>
          <Autocomplete
            multiple
            limitTags={2}
            disableCloseOnSelect
            options={attributesKeyValue}
            value={selectedAttributes}
            onChange={handleSelectAttributesChange}
            getOptionLabel={(option) => option.name}
            clearIcon={null}
            renderOption={(props, option, { selected }) => {
              return (
                <li {...props} key={option.uid}>
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {option.name}
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField {...params} label="Select Attribute" />
            )}
          />
        </Box>
        <Box
          display="flex"
          gap={2}
          alignItems="center"
          justifyContent="flex-end"
          sx={{ mb: 2 }}
        >
          <Button
            variant="contained"
            onClick={handleAddVariantRow}
            sx={{ mt: "10px" }}
            disabled={selectedAttributes.length === 0}
          >
            Add Variant
          </Button>
        </Box>
      </Box>

      {/* Variant Table */}
      {variants.length === 0 && !isItemWithVariant ? (
        <EmptyVariantState />
      ) : (
        <Box sx={{ width: "100%", overflowX: "auto" }}>
          <Table sx={{ minWidth: 900, tableLayout: "auto" }}>
            {/* minimum width for scroll */}
            <TableHead>
              <TableRow>
                <TableCell></TableCell>
                {selectedAttributes.map((attr) => (
                  <TableCell key={attr.uid} sx={{ minWidth: "180px" }}>
                    {attr.name}
                  </TableCell>
                ))}
                <TableCell sx={{ minWidth: "150px" }}>Price</TableCell>
                <TableCell sx={{ minWidth: "150px" }}>Sale Price</TableCell>
                <TableCell sx={{ minWidth: "150px" }}>SKU</TableCell>
                <TableCell sx={{ minWidth: "120px" }}>Quantity</TableCell>
                <TableCell sx={{ minWidth: "120px" }}>Item Unit</TableCell>
                <TableCell sx={{ minWidth: "140px" }}>Unit Type</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {variants.map((variant, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Avatar
                        variant="rounded"
                        src={variant.image || ""}
                        alt="Variant Image"
                        sx={{
                          width: 50,
                          height: 50,
                          cursor: "pointer",
                          border: "1px solid #ccc",
                          backgroundColor: "#fff",
                          color: "#000",
                        }}
                        onClick={() => handleUploadClick(index)}
                      >
                        {!variant.image && (
                          <Iconify icon="mdi:camera-plus-outline" width={24} />
                        )}
                      </Avatar>
                    </Box>
                  </TableCell>

                  {/* Attribute columns */}
                  {selectedAttributes.map((attr, attrIndex) => {
                    return (
                      <TableCell key={attr.uid}>
                        <Autocomplete
                          options={attr.attributeValues || []}
                          getOptionLabel={(option) =>
                            option.attribute_value || ""
                          }
                          value={getSelectedAttrValue(variant, attr)}
                          onChange={(e, newValue) =>
                            handleVariantAttributeValueChange(
                              index,
                              attr,
                              newValue
                            )
                          }
                          renderInput={(params) => (
                            <TextField {...params} size="small" />
                          )}
                        />
                      </TableCell>
                    );
                  })}

                  <TableCell>
                    <TextField
                      label="Price *"
                      fullWidth
                      size="small"
                      value={variant.original_price}
                      type="number"
                      name="original_price"
                      onChange={(event) => handleFormValueChange(event, index)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">₹</InputAdornment>
                        ),
                      }}
                    />
                  </TableCell>

                  <TableCell>
                    <TextField
                      label="Sale Price"
                      fullWidth
                      size="small"
                      value={variant.discounted_price}
                      type="number"
                      name="discounted_price"
                      onChange={(event) => handleFormValueChange(event, index)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">₹</InputAdornment>
                        ),
                      }}
                    />
                  </TableCell>

                  <TableCell>
                    <TextField
                      label="SKU"
                      fullWidth
                      size="small"
                      value={variant.sku_code || ""}
                      type="text"
                      name="sku_code"
                      onChange={(event) => handleFormValueChange(event, index)}
                    />
                  </TableCell>

                  <TableCell>
                    <TextField
                      label="Quantity"
                      fullWidth
                      size="small"
                      value={variant.combo_quantity || ""}
                      type="number"
                      name="combo_quantity"
                      onChange={(event) => handleFormValueChange(event, index)}
                    />
                  </TableCell>

                  <TableCell>
                    <TextField
                      label="Item Unit"
                      fullWidth
                      size="small"
                      value={variant.unit_value || ""}
                      type="number"
                      name="unit_value"
                      onChange={(event) => handleFormValueChange(event, index)}
                    />
                  </TableCell>

                  <TableCell>
                    <Autocomplete
                      fullWidth
                      size="small"
                      value={variant.selected_unit}
                      options={units}
                      getOptionLabel={(option) => option.unit_name || ""}
                      renderInput={(params) => (
                        <TextField {...params} label="Unit *" />
                      )}
                      onChange={(event, newValue) =>
                        handleFormValueChange(
                          {
                            target: { name: "selected_unit", value: newValue },
                          },
                          index
                        )
                      }
                    />
                  </TableCell>

                  <TableCell align="center">
                    <Tooltip title="Remove Variant">
                      <IconButton onClick={() => handleRemoveVariant(index)}>
                        <Iconify icon="eva:trash-2-outline" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {selectImageDialogOpen && (
        <ItemVariantImageUploadDialog
          selectImageDialogOpen={selectImageDialogOpen}
          selectImageCloseDialog={() => setSelectImageDialogOpen(false)}
        />
      )}
    </Box>
  );
};

export default ItemVariant;
